import { useState } from "react";
import { MessageCircle, X, Send, Users } from "lucide-react";
import TelegramFloat from "@/components/TelegramFloat";
import { useScrollFloatVisibility } from "@/hooks/useScrollFloatVisibility";

const WHATSAPP_NUMBER = "529371298761"; // +52 937 129 8761
const BUSINESS_NAME = "BHGC Nexus Logistics by El Cerrito de Tabasco SC de RL de CV";
const WHATSAPP_GROUP_URL = "https://chat.whatsapp.com/IFl4Pe4LF1q2dtbVR9R75m";

const QUICK_TOPICS = [
  "Quiero cotizar un envío",
  "Necesito ayuda con una guía",
  "Quiero levantar un reporte",
  "Tengo un problema con mi saldo",
  "Información sobre SaaS suscripciones",
  "Información sobre Franquicia White Label",
  "Información general"
];

function buildWhatsAppLink(message) {
  const text = encodeURIComponent(message || `Hola, soy cliente de ${BUSINESS_NAME}. Quisiera más información.`);
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
}

export default function WhatsAppFloat() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const showFab = useScrollFloatVisibility(open);

  const send = (preset) => {
    const msg = preset || message || "Hola, necesito ayuda.";
    const fullName = name ? ` Mi nombre es ${name}.` : "";
    const finalMsg = `${msg}${fullName}`;
    window.open(buildWhatsAppLink(finalMsg), "_blank", "noopener,noreferrer");
    if (!preset) setOpen(false);
  };

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-[calc(8.5rem+env(safe-area-inset-bottom))] right-4 lg:right-6 lg:bottom-24 z-50 w-[min(92vw,360px)] surface shadow-2xl flex flex-col max-h-[70vh] animate-in fade-in slide-in-from-bottom-2 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-border bg-[#075E54]">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-bold text-white text-sm">Soporte BHGC Nexus</div>
                <div className="text-[0.65rem] text-white/80 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-[#25D366] rounded-full inline-block" /> En línea · WhatsApp Business
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white p-1">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-ink-inset dark:bg-[#0D1117]">
            <div className="text-[0.7rem] text-center text-ink-muted">Hoy</div>

            <div className="max-w-[85%] bg-ink-surface dark:bg-[#212C33] text-ink-text text-sm px-3 py-2.5 rounded-lg rounded-tl-none">
              ¡Hola! 👋 Bienvenido a <strong>BHGC Nexus Logistics</strong>.
              <br />¿En qué podemos ayudarte hoy? Selecciona una opción o escribe tu mensaje.
            </div>

            {/* Menú: enlace al grupo de WhatsApp */}
            <a
              href={WHATSAPP_GROUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 text-left text-xs px-3 py-2.5 border border-[#25D366]/50 bg-[#25D366]/10 text-ink-text hover:bg-[#25D366]/20 rounded transition-colors"
            >
              <Users className="w-4 h-4 text-[#25D366] shrink-0" />
              <span className="flex-1">
                <span className="block font-bold text-[#25D366]">Unirse al grupo de WhatsApp</span>
                <span className="block text-[0.65rem] text-ink-muted">Comunidad y soporte de BHGC Nexus</span>
              </span>
            </a>

            {/* Quick topics */}
            <div className="flex flex-col gap-2">
              {QUICK_TOPICS.map((t) => (
                <button
                  key={t}
                  onClick={() => send(t)}
                  className="text-left text-xs px-3 py-2 border border-cyan/40 text-cyan bg-cyan/5 hover:bg-cyan/10 rounded transition-colors"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-ink-border bg-ink-surface space-y-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Tu nombre (opcional)"
              className="w-full bg-ink border border-ink-border px-3 py-2 text-sm focus:border-cyan focus:outline-none"
            />
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Escribe tu mensaje…"
                className="flex-1 bg-ink border border-ink-border px-3 py-2 text-sm focus:border-cyan focus:outline-none"
              />
              <button
                onClick={() => send()}
                disabled={!message.trim()}
                className="bg-[#25D366] hover:bg-[#1ebe5d] disabled:opacity-40 text-white px-3 py-2 transition-colors"
                aria-label="Enviar por WhatsApp"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[0.6rem] text-ink-muted text-center">
              Al enviar, se abrirá WhatsApp con tu mensaje listo para mandar al +52 937 129 8761
            </p>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen(v => !v)}
        aria-hidden={!showFab}
        className={`fixed bottom-[calc(7rem+env(safe-area-inset-bottom))] lg:bottom-6 right-4 lg:right-6 z-50 group transition-all duration-300 ${showFab ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        aria-label={open ? "Cerrar chat de soporte" : "Abrir chat de soporte por WhatsApp"}
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" style={{ animationDuration: "2s" }} />
        <span className="relative w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] flex items-center justify-center shadow-lg shadow-[#25D366]/40 transition-colors">
          {open ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          )}
        </span>
      </button>

      <TelegramFloat />
    </>
  );
}